<template>
    <section v-if="data && (data.applies || requestId)" class="submission-deadline" :class="[tone, {'submission-deadline--compact': compact}]" aria-live="polite">
        <header>
            <div class="submission-deadline__caption"><div class="submission-deadline__heading"><strong>{{ heading }}</strong><span v-if="urgencyLabel" class="submission-deadline__urgency">{{ urgencyLabel }}</span></div><div v-if="deadlineActive">до {{ data.effective_due_date }} · 23:59:59, UTC +3</div></div>
            <div class="submission-deadline__actions">
                <b-button v-if="requestId" size="sm" variant="outline-primary" :to="`/timesheets/${data.timesheet_id}`">Открыть табель</b-button>
                <b-button size="sm" variant="outline-secondary" :disabled="loading || busy" title="Обновить срок подачи"
                    aria-label="Обновить срок подачи" @click="refresh"><i class="fas fa-sync-alt" :class="{'fa-spin': loading}" aria-hidden="true"></i></b-button>
            </div>
        </header>
        <details v-if="deadlineActive && compact && deadlineState !== 'overdue'" class="submission-deadline__help">
            <summary>Условия подачи</summary>
            <p>После срока — только по согласованной заявке с объяснительной.</p>
        </details>
        <p v-else-if="deadlineActive" class="submission-deadline__warning"><i class="fas fa-exclamation-triangle" aria-hidden="true"></i> {{ deadlineState === 'overdue' ? 'Подача — только по согласованной заявке с объяснительной.' : 'После срока — только по согласованной заявке с объяснительной.' }}</p>
        <div v-if="requestId" class="mt-2">{{ data.object_name }} · {{ data.period_month }}<span v-if="data.responsible_name"> · Ответственный: {{ data.responsible_name }}</span></div>
        <p v-if="data.blocked_reason && !isSubmitted" class="mb-2">{{ data.blocked_reason }}</p>
        <p v-if="data.configuration_error" class="mb-2">{{ data.configuration_error }}</p>
        <p v-if="error" class="text-danger mb-2" role="alert">{{ error }}</p>
        <b-button v-if="data.can_request && !creating" variant="danger" :disabled="busy" @click="creating = true">Заявка на продление срока подачи</b-button>
        <form v-if="creating" class="submission-deadline__form" @submit.prevent="create">
            <label>Прошу продлить до <input v-model="requestedUntil" type="date" required :min="today" class="form-control"></label>
            <label>Причина <DiscussionEditor v-model="reason" required maxlength="4000" class="form-control"></DiscussionEditor></label>
            <div><b-button type="submit" variant="primary" :disabled="busy || !requestedUntil || !reason">Отправить заявку</b-button>
                <b-button variant="link" :disabled="busy" @click="creating = false">Отмена</b-button></div>
        </form>
        <article v-for="item in displayedRequests" :key="item.request_id" class="submission-deadline__request">
            <div><router-link :to="`/requests/${item.request_id}`">Заявка №{{ item.request_number || item.request_id }}</router-link>
                <strong class="ml-2">{{ statusLabel(item.display_status) }}</strong></div>
            <div>Запрошено до {{ item.requested_until }}. {{ item.reason }}</div>
            <small>Инициатор: {{ item.initiator_name }}</small>
            <div v-if="item.effective_until"><strong>Продлено до {{ item.effective_until }}, 23:59:59</strong></div>
            <div v-if="item.finish_reason">{{ item.finish_reason }}</div>
            <div v-if="item.display_status === 'pending'">Согласовали {{ item.decisions.filter(d => d.status === 'approved').length }} из {{ item.decisions.length }}</div>
            <ul class="mb-2 mt-2">
                <li v-for="decision in item.decisions" :key="decision.participant_id">
                    {{ decision.name }} — {{ statusLabel(decision.status) }}<span v-if="decision.approved_until"> до {{ decision.approved_until }}, 23:59:59</span>
                    <span v-if="decision.comment"> · {{ decision.comment }}</span>
                    <b-button v-if="item.can_replace" size="sm" variant="link" :disabled="busy" @click="startReplacement(item, decision)">Заменить</b-button>
                </li>
            </ul>
            <div v-if="item.can_decide" class="my-2">
                <b-button variant="success" :disabled="busy" @click="openDecision(item, 'approve')">Согласовать</b-button>
                <b-button variant="outline-danger" :disabled="busy" @click="openDecision(item, 'reject')">Отказать</b-button>
            </div>
            <b-button v-if="item.can_cancel" size="sm" variant="outline-secondary" :disabled="busy" @click="decide(item, 'cancel')">Отозвать заявку</b-button>
        </article>
        <b-button v-if="!requestId && (data.requests || []).length > 1" size="sm" variant="link" @click="showHistory = !showHistory">{{ showHistory ? 'Скрыть прошлые попытки' : 'История продлений' }}</b-button>
        <form v-if="replacement" class="submission-deadline__form" @submit.prevent="replace">
            <strong>Заменить {{ replacement.name }}. Все решения будут сброшены.</strong>
            <label>Новый согласующий <select v-model="replacement.user_id" required class="form-control">
                <option value="">Выберите сотрудника</option><option v-for="user in data.replacement_users" :key="user.user_id" :value="user.user_id">{{ user.last_name }} {{ user.first_name }} {{ user.middle_name }}</option>
            </select></label>
            <label>Причина замены <DiscussionEditor v-model="replacement.reason" required maxlength="4000" class="form-control"></DiscussionEditor></label>
            <div><b-button type="submit" variant="primary" :disabled="busy">Заменить и начать согласование заново</b-button>
                <b-button variant="link" :disabled="busy" @click="replacement = null">Отмена</b-button></div>
        </form>
        <b-modal v-model="decisionVisible" :title="decisionForm.action === 'reject' ? 'Отказ в продлении' : 'Согласование продления'" :no-close-on-backdrop="busy" :no-close-on-esc="busy" :hide-header-close="busy">
            <template v-if="decisionItem">
                <p>Инициатор просит продлить до <strong>{{ decisionItem.requested_until }}, 23:59:59</strong> (UTC +3).</p>
                <p>{{ decisionItem.reason }}</p>
                <template v-if="decisionForm.action === 'approve'">
                    <b-form-checkbox v-model="decisionForm.change_date" :disabled="busy">Указать другую дату</b-form-checkbox>
                    <b-form-group v-if="decisionForm.change_date" label="Продлить до" class="mt-3">
                        <b-form-input v-model="decisionForm.approved_until" type="date" :min="today" :disabled="busy" required />
                    </b-form-group>
                    <p v-if="!approvalUntil || approvalUntil < today" class="text-danger mt-2">Запрошенный срок уже прошёл. Укажите будущую дату для согласования.</p>
                    <p v-else class="mt-3 mb-2">Вы разрешаете подачу до <strong>{{ approvalUntil }}, 23:59:59</strong> (UTC +3).</p>
                    <small>После всех согласований действует самая ранняя из одобренных дат. Можно указать дату раньше или позже запрошенной.</small>
                </template>
                <b-form-group :label="decisionForm.action === 'reject' ? 'Причина отказа (обязательно)' : 'Комментарий (необязательно)'" class="mt-3">
                    <DiscussionEditor v-model="decisionForm.comment" maxlength="4000" :disabled="busy" />
                </b-form-group>
            </template>
            <p v-else>Состояние заявки изменилось. Закройте окно и проверьте решения участников.</p>
            <p v-if="error" class="text-danger" role="alert">{{ error }}</p>
            <template #modal-footer>
                <b-button variant="secondary" :disabled="busy" @click="decisionVisible = false">Отмена</b-button>
                <b-button :variant="decisionForm.action === 'reject' ? 'danger' : 'success'" :disabled="busy || !canConfirmDecision" @click="confirmDecision">{{ decisionForm.action === 'reject' ? 'Отказать' : 'Согласовать' }}</b-button>
            </template>
        </b-modal>
    </section>
</template>

<script>
import api from '@/utils/api';
import {socket} from '@/plugins/socket';

export default {
    name: 'TimesheetSubmissionDeadline',
    props: {value: Object, timesheetId: [Number, String], requestId: [Number, String], compact: Boolean},
    data() { return {data: null, error: '', busy: false, loading: false, creating: false, requestedUntil: '', reason: '',
        decisionVisible: false, decisionForm: {}, replacement: null, showHistory: false, timer: null, clockTimer: null, pollTimer: null, clockOffset: 0, clock: Date.now(), loadSequence: 0}; },
    computed: {
        today() { return new Date(this.clock + 3 * 3600000).toISOString().slice(0, 10); },
        isSubmitted() { return this.data && !['draft', 'returned', 'hr_editing'].includes(this.data.sheet_status); },
        deadlineActive() { return Boolean(this.data && this.data.applies && !this.isSubmitted && this.data.sheet_status !== 'hr_editing'); },
        remainingMs() {
            if (!this.deadlineActive) return null;
            const end = this.data.exclusive_end ? Date.parse(this.data.exclusive_end)
                : Date.parse(this.data.effective_due_date + 'T00:00:00+03:00') + 86400000;
            return Number.isFinite(end) ? end - this.clock : null;
        },
        deadlineState() {
            if (this.remainingMs === null) return 'notice';
            if (this.remainingMs <= 0) return 'overdue';
            if (this.remainingMs <= 12 * 3600000) return 'critical';
            if (this.remainingMs <= 24 * 3600000) return 'urgent';
            if (this.remainingMs <= 72 * 3600000) return 'soon';
            return 'notice';
        },
        urgencyLabel() {
            if (!this.deadlineActive || this.deadlineState === 'notice') return '';
            if (this.deadlineState === 'overdue') return 'Срок истёк';
            const totalMinutes = Math.ceil(this.remainingMs / 60000);
            const days = this.remainingMs > 86400000 ? Math.floor(totalMinutes / 1440) : 0;
            const hours = Math.floor((totalMinutes - days * 1440) / 60);
            const minutes = totalMinutes % 60;
            return 'Осталось ' + [days ? `${days} дн.` : '', hours ? `${hours} ч.` : '', minutes ? `${minutes} мин.` : ''].filter(Boolean).join(' ');
        },
        tone() { return this.deadlineActive ? 'is-' + this.deadlineState : 'is-submitted'; },
        heading() {
            if (!this.data.applies) return 'Требование срока подачи отключено';
            if (this.data.sheet_status === 'hr_editing') return 'Табель исправляет табельщик';
            if (this.isSubmitted) return 'Табель подан' + (this.data.submitted_at ? ' · ' + new Date(this.data.submitted_at).toLocaleString('ru-RU', {timeZone: 'Europe/Minsk'}) + ', UTC +3' : '');
            if (this.data.effective_due_date > this.data.base_due_date) return this.data.effective_due_date < this.today ? 'Продлённый срок подачи истёк' : 'Подача разрешена до ' + this.data.effective_due_date;
            return this.data.is_correction ? 'Срок подачи исправлений' : 'Срок подачи табеля';
        },
        displayedRequests() {
            const rows = this.data && this.data.requests || [];
            if (this.requestId) return rows.filter(row => Number(row.request_id) === Number(this.requestId));
            return this.showHistory ? rows : rows.slice(0, 1);
        },
        decisionItem() { return (this.data && this.data.requests || []).find(item => Number(item.request_id) === Number(this.decisionForm.request_id) && item.can_decide); },
        approvalUntil() { return this.decisionForm.change_date ? this.decisionForm.approved_until : this.decisionItem && this.decisionItem.requested_until; },
        canConfirmDecision() { return Boolean(this.decisionItem && (this.decisionForm.action === 'reject' ? this.decisionForm.comment : this.approvalUntil && this.approvalUntil >= this.today)); }
    },
    watch: {
        value: {immediate: true, handler(value) { if (value) this.accept(value); }},
        requestId(value, previous) { if (previous) socket.emit('timesheet-extension:unsubscribe', {request_id: previous}); this.decisionVisible = false; this.load(); this.subscribeRequest(); },
        timesheetId() { this.creating = false; this.replacement = null; this.decisionVisible = false; this.load(); }
    },
    mounted() {
        if (!this.value) this.load();
        socket.on('timesheet:changed', this.onRealtime);
        socket.on('connect', this.subscribeRequest);
        socket.on('timesheet:extension-changed', this.onRequestRealtime);
        socket.on('timesheet:extension-access-revoked', this.onRequestRevoked);
        this.subscribeRequest();
        document.addEventListener('visibilitychange', this.onVisibility);
        window.addEventListener('focus', this.refresh);
        this.schedulePoll();
    },
    beforeDestroy() {
        this.loadSequence += 1;
        clearTimeout(this.timer);
        clearTimeout(this.clockTimer);
        clearTimeout(this.pollTimer);
        socket.off('timesheet:changed', this.onRealtime);
        socket.off('connect', this.subscribeRequest);
        socket.off('timesheet:extension-changed', this.onRequestRealtime);
        socket.off('timesheet:extension-access-revoked', this.onRequestRevoked);
        if (this.requestId) socket.emit('timesheet-extension:unsubscribe', {request_id: this.requestId});
        document.removeEventListener('visibilitychange', this.onVisibility);
        window.removeEventListener('focus', this.refresh);
    },
    methods: {
        statusLabel(status) { return {pending: 'Ожидает решения', approved: 'Согласовано', rejected: 'Отказано', cancelled: 'Отозвана / закрыта', expired: 'Срок истёк'}[status] || status; },
        updateClock() {
            this.clock = Date.now() + this.clockOffset;
            clearTimeout(this.clockTimer);
            if (this.remainingMs > 0) this.clockTimer = setTimeout(this.updateClock, Math.min(60000, this.remainingMs));
        },
        accept(value) {
            const serverTime = Date.parse(value.server_time);
            if (Number.isFinite(serverTime) && (!this.data || this.data.server_time !== value.server_time)) this.clockOffset = serverTime - Date.now();
            this.data = value;
            this.updateClock();
            clearTimeout(this.timer);
            const boundaries = [value.exclusive_end && Date.parse(value.exclusive_end)];
            (value.requests || []).filter(item => item.display_status === 'pending').forEach(item => item.decisions.forEach(d => {
                if (d.approved_until) boundaries.push(Date.parse(d.approved_until + 'T00:00:00+03:00') + 86400000);
            }));
            boundaries.push(Date.parse(this.today + 'T00:00:00+03:00') + 86400000);
            const next = Math.min(...boundaries.filter(time => time > this.clock));
            this.timer = setTimeout(() => {
                this.clock = Date.now() + this.clockOffset;
                if (value.exclusive_end && this.clock >= Date.parse(value.exclusive_end)) this.$emit('change', Object.assign({}, this.data, {can_submit: false}));
                this.load();
            }, Math.min(Math.max(next - this.clock + 20, 20), 2147483647));
        },
        async load() {
            if (!this.requestId && !this.timesheetId) return;
            const seq = ++this.loadSequence;
            this.loading = true;
            try {
                const url = this.requestId ? `/api/requests/items/${this.requestId}/submission-extension` : `/api/timesheets/${this.timesheetId}/submission-deadline`;
                const result = await api.get(url, {ignoreModal: true});
                if (seq !== this.loadSequence) return;
                this.error = '';
                this.accept(result.data);
                this.$emit('change', result.data);
            } catch (error) {
                if (seq !== this.loadSequence) return;
                this.error = this.message(error);
                if (error.response && [403, 404].includes(error.response.status)) { this.data = null; this.$emit('access-lost'); }
            } finally { if (seq === this.loadSequence) this.loading = false; }
        },
        onRealtime() { this.load(); },
        subscribeRequest() {
            if (!this.requestId) return;
            if (!socket.connected) { socket.connect(); return; }
            const token = window.localStorage.getItem('token');
            if (token) socket.emit('timesheet-extension:subscribe', {token, request_id: this.requestId}, result => {
                if (result && result.ok) this.load();
            });
        },
        onRequestRealtime(event) { if (Number(event.request_id) === Number(this.requestId)) this.load(); },
        onRequestRevoked(event) { if (Number(event.request_id) === Number(this.requestId)) { this.data = null; this.$emit('access-lost'); this.$emit('saved'); } },
        refresh() { this.updateClock(); if (!this.loading) return this.load(); },
        schedulePoll() {
            clearTimeout(this.pollTimer);
            if (this._isDestroyed || this._isBeingDestroyed) return;
            this.pollTimer = setTimeout(this.poll, 5 * 60000);
        },
        async poll() {
            try {
                if (!document.hidden && !this.loading && !this.busy) await this.refresh();
            } finally { this.schedulePoll(); }
        },
        onVisibility() { if (!document.hidden) return this.refresh(); },
        message(error) { const data = error.response && error.response.data || {}; return data.msg || data.message || 'Не удалось выполнить действие.'; },
        async run(action) {
            if (this.busy) return;
            this.busy = true; this.error = '';
            try { await action(); this.creating = false; this.replacement = null; await this.load(); this.$emit('saved'); return true; }
            catch (error) { this.error = this.message(error); await this.load(); }
            finally { this.busy = false; }
        },
        create() { return this.run(() => api.post(`/api/timesheets/${this.data.timesheet_id}/submission-extension-requests`, {requested_until: this.requestedUntil, reason: this.reason}, {ignoreModal: true})); },
        openDecision(item, action) {
            this.error = '';
            this.decisionForm = {request_id: item.request_id, action, change_date: false, approved_until: item.requested_until, comment: ''};
            this.decisionVisible = true;
        },
        async confirmDecision() {
            if (!this.canConfirmDecision) return;
            const form = this.decisionForm;
            const payload = {comment: form.comment};
            if (form.action === 'approve') payload.approved_until = this.approvalUntil;
            if (await this.run(() => api.post(`/api/requests/items/${form.request_id}/${form.action}`, payload, {ignoreModal: true}))) this.decisionVisible = false;
        },
        decide(item, action) { return this.run(() => api.post(`/api/requests/items/${item.request_id}/${action}`, {}, {ignoreModal: true})); },
        startReplacement(item, decision) { this.replacement = {request_id: item.request_id, participant_id: decision.participant_id, name: decision.name, user_id: '', reason: ''}; },
        replace() { const r = this.replacement; return this.run(() => api.post(`/api/requests/items/${r.request_id}/approvers/${r.participant_id}/replace`, {user_id: r.user_id, reason: r.reason}, {ignoreModal: true})); }
    }
};
</script>

<style scoped>
.submission-deadline { border: 2px solid #b9a66d; border-left-width: 7px; background: #fffaf0; color: #513700; padding: 10px 14px; border-radius: 8px; margin: 12px 0; }
.submission-deadline header { display: flex; justify-content: space-between; align-items: center; gap: 12px; }
.submission-deadline header strong { font-size: 1.15rem; }
.submission-deadline__actions { display: flex; align-items: center; gap: 8px; flex-shrink: 0; }
.submission-deadline__heading { display: flex; flex-wrap: wrap; align-items: center; gap: 4px 10px; }
.submission-deadline__urgency { font-size: .8rem; font-weight: 700; border: 1px solid currentColor; border-radius: 4px; padding: 1px 6px; }
.submission-deadline__warning { margin: 4px 0 0; padding: 3px 6px; border-radius: 4px; font-size: .82rem; line-height: 1.35; }
.submission-deadline__warning i { margin-right: 3px; }
.submission-deadline.is-soon { border-color: #c78a00; background: #fff4c7; }
.submission-deadline.is-soon .submission-deadline__warning { font-weight: 600; }
.submission-deadline.is-urgent { border-color: #c32335; background: #ffe4e7; color: #78101d; }
.submission-deadline.is-urgent .submission-deadline__warning { font-weight: 700; }
.submission-deadline.is-critical, .submission-deadline.is-overdue { border-color: #a71d2a; background: #ffdde1; color: #78101d; }
.submission-deadline.is-critical .submission-deadline__warning, .submission-deadline.is-overdue .submission-deadline__warning,
.submission-deadline.is-critical .submission-deadline__urgency, .submission-deadline.is-overdue .submission-deadline__urgency { background: #a71d2a; color: #fff; font-weight: 700; }
.submission-deadline.is-overdue .submission-deadline__urgency { text-transform: uppercase; }
.submission-deadline.is-submitted { border-color: #31916a; background: #eaf7f0; color: #185738; }
.submission-deadline__request { margin-top: 12px; padding: 12px; background: rgba(255,255,255,.7); border-radius: 6px; color: #24313e; }
.submission-deadline__form { display: grid; gap: 8px; max-width: 700px; margin: 12px 0; }
.submission-deadline__form label { margin: 0; }
.submission-deadline__form input[type=date] { max-width: 230px; }
.submission-deadline__form button { margin: 3px; }
@media (max-width: 600px) { .submission-deadline { padding: 10px; } .submission-deadline header { align-items: flex-start; flex-direction: column; } }
.submission-deadline--compact { padding: .4rem .6rem; margin-bottom: .4rem; display: flex; flex-wrap: wrap; align-items: center; gap: .3rem .8rem; }
.submission-deadline--compact > header { flex: 1 1 auto; gap: .5rem; }
.submission-deadline--compact .submission-deadline__caption { display: flex; align-items: center; flex-wrap: wrap; gap: .25rem .6rem; font-size: .85rem; }
.submission-deadline--compact .submission-deadline__help { font-size: .8rem; }
.submission-deadline--compact .submission-deadline__help summary { cursor: pointer; }
.submission-deadline--compact .submission-deadline__help[open],
.submission-deadline--compact .submission-deadline__form,
.submission-deadline--compact .submission-deadline__request { flex-basis: 100%; }
.submission-deadline--compact .submission-deadline__help p { margin: .4rem 0; }
</style>
